import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiPlus, FiEdit, FiTrash2, FiEye, FiLayout } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../utils/api'
import { useAuth } from '../App'

export default function DashboardList() {
  const { user, selectedCompanyId } = useAuth()
  const [dashboards, setDashboards] = useState([])
  const [showCreate, setShowCreate] = useState(false)
  const [editTarget, setEditTarget] = useState(null) // dolu ise modal "adi duzenle" modunda
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const navigate = useNavigate()

  useEffect(() => { loadDashboards() }, [selectedCompanyId])

  const loadDashboards = async () => {
    try {
      const { data } = await api.get('/dashboards')
      // _Sistem dashboard'u UI'da gozukmesin (sadece izin yonetimi icin)
      const liste = data.filter(d => !String(d.name || '').startsWith('_'))
      // Firma kullanicisinin tek panosu varsa listeyi gostermeden dogrudan panoya
      if (!user?.is_admin && liste.length === 1) { navigate(`/dashboard/${liste[0].id}`, { replace: true }); return }
      setDashboards(liste)
    } catch (err) {
      toast.error('Dashboardlar yüklenemedi')
    }
  }

  const createDashboard = async (e) => {
    e.preventDefault()
    try {
      if (editTarget) {
        await api.put(`/dashboards/${editTarget.id}`, { name, description })
        toast.success('Dashboard güncellendi')
      } else {
        const { data } = await api.post('/dashboards', { name, description, company_id: selectedCompanyId })
        toast.success('Dashboard oluşturuldu')
        navigate(`/designer/${data.id}`)
      }
      setShowCreate(false)
      setEditTarget(null)
      setName('')
      setDescription('')
      loadDashboards()
    } catch (err) {
      toast.error(editTarget ? 'Güncelleme başarısız' : 'Oluşturma başarısız')
    }
  }

  const openEdit = (d) => {
    setEditTarget(d)
    setName(d.name || '')
    setDescription(d.description || '')
    setShowCreate(true)
  }

  const deleteDashboard = async (id) => {
    if (!confirm('Bu dashboard silinecek. Emin misiniz?')) return
    try {
      await api.delete(`/dashboards/${id}`)
      toast.success('Dashboard silindi')
      loadDashboards()
    } catch (err) {
      toast.error('Silme başarısız')
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboardlar</h1>
          <p className="text-sm text-gray-500 mt-1">Raporlarınızı görüntüleyin ve yönetin</p>
        </div>
        {user?.is_admin && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
          >
            <FiPlus /> Yeni Dashboard
          </button>
        )}
      </div>

      {/* Dashboard Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {dashboards.map((d) => (
          <div key={d.id} onClick={() => navigate(`/dashboard/${d.id}`)}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md hover:border-primary-200 transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                  <FiLayout className="text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-1.5">
                    {d.name}
                    {user?.is_admin && (
                      <button onClick={(e) => { e.stopPropagation(); openEdit(d) }} title="Adı ve açıklamayı düzenle"
                        className="text-gray-300 hover:text-primary-600">
                        <FiEdit size={13} />
                      </button>
                    )}
                  </h3>
                  <p className="text-xs text-gray-400">{d.page_count} sayfa</p>
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${d.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {d.is_published ? 'Yayında' : 'Taslak'}
              </span>
            </div>

            {d.description && (
              <p className="text-sm text-gray-500 mt-3">{d.description}</p>
            )}

            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-50">
              <button
                onClick={() => navigate(`/dashboard/${d.id}`)}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
              >
                <FiEye /> Görüntüle
              </button>
              {user?.is_admin && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/designer/${d.id}`) }}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 ml-auto"
                  >
                    <FiEdit /> Düzenle
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteDashboard(d.id) }}
                    className="flex items-center gap-1 text-sm text-red-400 hover:text-red-600"
                  >
                    <FiTrash2 />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}

        {dashboards.length === 0 && (
          <div className="col-span-full text-center py-20 text-gray-400">
            Henüz dashboard oluşturulmamış
          </div>
        )}
      </div>

      {/* Oluşturma Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowCreate(false); setEditTarget(null) }}>
          <form onSubmit={createDashboard} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-4">{editTarget ? `Düzenle: ${editTarget.name}` : 'Yeni Dashboard'}</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Dashboard Adı</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                placeholder="Satış Raporu" required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
              <textarea
                value={description} onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                rows={2} placeholder="Opsiyonel"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => { setShowCreate(false); setEditTarget(null) }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
              <button type="submit" className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">{editTarget ? 'Kaydet' : 'Oluştur'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
